import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, pointerWithin, rectIntersection } from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import {
	ActionButtonWapper,
	AddButtonIcon,
	Board,
	ButtonFilterWapper,
	CardMeta,
	CardTitle,
	Columns,
	FilterButtonIcon,
	FilterInput,
	FilterInputWapper,
	MobileColumnDot,
	MobileColumnDots,
	MobileColumnNav,
	MobileNavBtn,
	OverlayCard,
	OverlayColumn,
	Page,
	SearchIcon,
	SearchWrapper,
	TopBar,
} from './styles'

import { clamp, findColumnIdByCardId, leadMatchesSearch, useAutoScroll } from './Helpers'
import { getAllLeadOrganisations } from '@data/conn/get/lead'
import { TOAST_TYPE, useTableReducer } from '@base'
import { MultiSelect } from '@baseV2'
import { DRAWER_TYPES, LeadFilter, MB_ROLES, searchFilter, strings } from '@constants'
import Column from './components/Column'
import { useForceUpdate, useHeaderMeta } from '@hooks'
import { updateStatus } from '@data/conn/put/leads'
import { showLoader, showToast } from '@data/state/action'
import { useDispatch, useSelector } from 'react-redux'
import { isSalesAdmin, isSupplyAdmin } from 'src/utils/adminFilter'
import CardTwo from './components/CardTwo'
import FilterIcon from '@assets/icons/FilterIcon'
import searchIcon from '@assets/icons/search.svg'
import { Add } from '@material-ui/icons'
import { toggleDrawer } from '@data/state/action/root'
import { getAllEmployees } from '@data'
import { displayName } from '@utils'

const initial = {
	columns: {
		OPEN_NEW: { id: 'OPEN_NEW', title: 'Un Assigned', status: 'open_new', cardIds: [] },
		OPEN: { id: 'OPEN', title: 'New', status: 'active', cardIds: [] },
		WARM: { id: 'WARM', title: 'Warm', status: '', cardIds: [] },
		HOT: { id: 'HOT', title: 'Hot', status: 'failure', cardIds: [] },
		WON: { id: 'WON', title: 'Won', status: 'success', cardIds: [] },
		LOST: { id: 'LOST', title: 'Lost', status: 'commodity', cardIds: [] },
	},
	cards: {},
	columnOrder: ['OPEN', 'WARM', 'HOT', 'WON', 'LOST', 'OPEN_NEW'],
}

export const orderStatuses = [{ name: 'OPEN' }, { name: 'WARM' }, { name: 'HOT' }, { name: 'WON' }, { name: 'LOST' }, { name: 'OPEN_NEW' }] || []

// The API returns lead.status as a backend value ('active', 'failure', 'success', 'commodity',
// 'open_new', or '' for Warm). After an in-app drag we instead store the column id ('OPEN', ...).
// Map either form to the matching column id so each card lands in its own column instead of
// falling through to OPEN.
const STATUS_TO_COLUMN_ID = Object.values(initial.columns).reduce((map, col) => {
	map[(col.status || '').toUpperCase()] = col.id
	return map
}, {})

const resolveColumnId = (lead) => {
	const raw = (lead?.status ?? lead?.lead_status ?? '').toString().toUpperCase()
	if (!raw) return null // null / empty status -> not shown in any column
	if (initial.columns[raw]) return raw // already a column id (set locally after a drag)
	return STATUS_TO_COLUMN_ID[raw] // backend status value
}

const SEARCH_PLACEHOLDERS = ['organisation name', 'organisation id', 'pan', 'location', 'source', 'mobile number']

// Isolated so the 2s placeholder rotation re-renders only this input, not the whole board.
const SearchField = React.memo(function SearchField({ value, onChange }) {
	const [placeholderIndex, setPlaceholderIndex] = useState(0)

	useEffect(() => {
		const interval = setInterval(() => {
			setPlaceholderIndex((i) => (i + 1) % SEARCH_PLACEHOLDERS.length)
		}, 2000)
		return () => clearInterval(interval)
	}, [])

	return <FilterInput value={value} onChange={onChange} placeholder={`Search for ${SEARCH_PLACEHOLDERS[placeholderIndex]}`} />
})

// ---------- main ----------
export default function KanbanBoard() {
	const [board, setBoard] = useState(initial)
	const [active, setActive] = useState(null)
	const [indicator, setIndicator] = useState(null)
	const [arrangeColumn, setArrangeColumn] = useState(null)
	const [salesAdmin, setSalesAdmin] = useState([])
	const [currentColIdx, setCurrentColIdx] = useState(0)
	const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 424px)').matches)

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 424px)')
		const handler = (e) => setIsMobile(e.matches)
		mq.addEventListener('change', handler)
		return () => mq.removeEventListener('change', handler)
	}, [])
	const globalDispatch = useDispatch()
	const [forceUpdate, update] = useForceUpdate()
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))
	const isRestrictedRole = isSalesAdmin(loggedInUser?.role, false) || isSupplyAdmin(loggedInUser?.role, false)

	const boardRef = useRef(null)
	const columnsScrollRefs = useRef({})
	const [listData, SetListData] = useState([])
	const [search, setSearch] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')

	// Debounce search so filtering / card re-render runs once per pause, not per keystroke.
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(search), 250)
		return () => clearTimeout(t)
	}, [search])

	const handleSearchChange = useCallback((val) => setSearch(val || ''), [])

	const { start, stop, updatePointer } = useAutoScroll()
	const [state, dispatch, actions] = useTableReducer(LeadFilter)
	useHeaderMeta({
		title: strings('all', 'leads'),
	})

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

	const columns = useMemo(() => {
		const order = Array.isArray(arrangeColumn) && arrangeColumn.length > 0 ? arrangeColumn.map((s) => s.name) : board.columnOrder
		return order
			.filter((id) => !(isRestrictedRole && id === 'OPEN_NEW'))
			.map((id) => board.columns[id])
			.filter(Boolean)
	}, [board, arrangeColumn, isRestrictedRole])

	useEffect(() => {
		setCurrentColIdx((i) => Math.min(i, Math.max(0, columns.length - 1)))
	}, [columns.length])

	const activeCard = active?.data?.current?.type === 'card' ? board.cards[active.id] : null
	const activeColumn = active?.data?.current?.type === 'column' ? board.columns[active.id] : null

	/// filter data end

	const registerScrollEl = useCallback((columnId, el) => {
		if (el) columnsScrollRefs.current[columnId] = el
	}, [])

	// `update` from useForceUpdate is recreated every render; wrap it in a stable
	// reference so memoized Column/Card don't re-render just because its identity changed.
	const updateRef = useRef(update)
	updateRef.current = update
	const stableUpdate = useCallback(() => updateRef.current(), [])

	useEffect(() => {
		let cancelled = false
		globalDispatch(showLoader(true))
		dispatch(actions.fetchTableDataProgress())
		const empId = null
		getAllLeadOrganisations(state.query.page, empId, state.query.filters)
			.then((response) => {
				if (cancelled) return
				SetListData(response.data)
				dispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((err) => {
				if (cancelled) return
				dispatch(actions.fetchTableDataFailure(err))
			})
			.finally(() => {
				if (!cancelled) globalDispatch(showLoader(false))
			})
		return () => {
			cancelled = true
		}
	}, [state.query, forceUpdate])

	useEffect(() => {
		dispatch(showLoader(true))
		getAllEmployees({ roles: `${MB_ROLES.SALES}, ${MB_ROLES.SUPPLY}` })
			.then((response) => {
				const user = response?.map((item) => {
					item['display_name'] = displayName(item)
					return item
				})
				setSalesAdmin(user)
			})
			.catch((err) => {
				dispatch(showToast(true, strings('msg_could_not_fetch_reporting_managers'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [])

	// Build cards ONCE when listData comes (do NOT rebuild on search)

	const filteredListData = useMemo(() => {
		if (!Array.isArray(listData)) return []
		return listData
	}, [listData])

	useEffect(() => {
		setBoard((prev) => {
			const cards = {}
			const cardIds = []
			const columnCardMap = {
				OPEN: [],
				WARM: [],
				HOT: [],
				WON: [],
				LOST: [],
				OPEN_NEW: [],
			}

			filteredListData.forEach((lead) => {
				const id = lead.id
				const status = resolveColumnId(lead)
				if (!status) return // skip leads with null / empty status — not shown in any column
				cardIds.push(id)

				cards[id] = {
					id,
					raw: lead,
					created_at: lead?.created_at,
					organisation_name: lead?.organisation_name || 'Org Name: NA',
					organisation_id: lead?.organisation_id || 'Org Id: NA',
					pan: lead?.pan || 'PAN: NA',
					roles: lead?.roles || [],
					location: lead?.location || ': NA',
					category_items: lead?.category_items || [],
					status,
					lead_id: lead?.lead_id || null,
					updated_at: lead?.updated_at || null,
				}

				if (columnCardMap[status]) {
					columnCardMap[status].push(id)
				} else {
					columnCardMap.OPEN.push(id) // fallback safety
				}
			})

			return {
				...prev,
				cards,
				columns: {
					OPEN: { ...prev.columns.OPEN, cardIds: columnCardMap.OPEN },
					WARM: { ...prev.columns.WARM, cardIds: columnCardMap.WARM },
					HOT: { ...prev.columns.HOT, cardIds: columnCardMap.HOT },
					WON: { ...prev.columns.WON, cardIds: columnCardMap.WON },
					LOST: { ...prev.columns.LOST, cardIds: columnCardMap.LOST },
					OPEN_NEW: { ...prev.columns.OPEN_NEW, cardIds: columnCardMap.OPEN_NEW },
				},
			}
		})
	}, [filteredListData])

	// Filter cards ONLY for rendering (do not change board state)
	const visibleCardsByColumn = useMemo(() => {
		const result = {}
		for (const colId of board.columnOrder) {
			const ids = board.columns[colId]?.cardIds
			const visible = ids
				.map((id) => board.cards[id])
				.filter(Boolean)
				.filter((c) => leadMatchesSearch(c, debouncedSearch))
			result[colId] = visible
		}
		return result
	}, [board, debouncedSearch])

	function computeIndicator({ active, over, board }) {
		if (!active || !over) return null
		if (active.data.current?.type !== 'card') return null

		const overType = over.data.current?.type

		if (overType === 'column-drop') {
			const colId = over.data.current.columnId
			const len = board.columns[colId]?.cardIds?.length ?? 0
			return { columnId: colId, index: len }
		}

		if (overType === 'card') {
			const colId = findColumnIdByCardId(board, over.id)
			if (!colId) return null
			const idx = board.columns[colId].cardIds.indexOf(over.id)
			return { columnId: colId, index: clamp(idx, 0, board.columns[colId].cardIds.length) }
		}

		return null
	}

	const changeStatusHandler = (leadId, toColId, prevBoardState) => {
		globalDispatch(showLoader(true))
		updateStatus(leadId, toColId)
			.then(() => {
				SetListData((prev) =>
					prev.map((lead) =>
						lead.id === active.id
							? {
									...lead,
									status: toColId,
									lead_status: toColId,
								}
							: lead
					)
				)
				globalDispatch(showToast(true, `Done! Card updated`, { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => {
				globalDispatch(showToast(true, `${err?.response?.data?.message}`, { type: TOAST_TYPE.ERROR }))
				setBoard(prevBoardState)
			})
			.finally(() => {
				globalDispatch(showLoader(false))
			})
	}

	const filterHandler = () => {
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.OPEN_SEARCH_PANEL, { searchType: searchFilter.ORG_LEADS }))
	}

	const addLeadHandler = () => {
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.ADD_LEAD, { label: strings('add', 'lead'), isEdit: false, update }))
	}

	return (
		<Page>
			<TopBar>
				<SearchWrapper>
					<FilterInputWapper>
						<SearchField value={search} onChange={handleSearchChange} />
						<SearchIcon>
							<img src={searchIcon} alt='searchIcon' />
						</SearchIcon>
					</FilterInputWapper>
				</SearchWrapper>
				<ActionButtonWapper>
					<MultiSelect
						id={'arrange-column'}
						label={'Show Column'}
						placeholder={'select show column'}
						value={arrangeColumn || []}
						primaryKey={'name'}
						displayKey={'name'}
						data={isRestrictedRole ? orderStatuses.filter((s) => s.name !== 'OPEN_NEW') : orderStatuses}
						onChange={(val) => setArrangeColumn(val)}
					/>
					<ButtonFilterWapper>
						<FilterButtonIcon variant='outline' onClick={filterHandler}>
							<FilterIcon />
							Filter
						</FilterButtonIcon>
						<AddButtonIcon onClick={addLeadHandler}>
							<Add /> Add Leads
						</AddButtonIcon>
					</ButtonFilterWapper>
				</ActionButtonWapper>
			</TopBar>

			<DndContext
				sensors={sensors}
				collisionDetection={(args) => {
					const pointer = pointerWithin(args)
					return pointer.length ? pointer : rectIntersection(args)
				}}
				onDragStart={({ active }) => {
					setActive(active)
					start({ boardEl: null, columnsEls: Object.values(columnsScrollRefs.current) })
				}}
				onDragMove={({ delta, activatorEvent }) => {
					// Cheap path: runs on every pointer move. Only update the autoscroll pointer here —
					// no setState, no O(n) board scan. activatorEvent is the drag-start event, so add
					// delta to get the live pointer (this also fixes autoscroll, which was using a stale point).
					const e = activatorEvent
					const base = e?.touches?.[0] ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e?.clientX ?? 0, y: e?.clientY ?? 0 }
					updatePointer(base.x + (delta?.x ?? 0), base.y + (delta?.y ?? 0))
				}}
				onDragOver={({ active, over }) => {
					// Fires only when the hovered target changes (not every pixel). Compute the drop
					// indicator here and skip the state update when it hasn't actually moved.
					const next = computeIndicator({ active, over, board })
					setIndicator((prev) => (prev?.columnId === next?.columnId && prev?.index === next?.index ? prev : next))
				}}
				onDragCancel={() => {
					setActive(null)
					setIndicator(null)
					stop()
				}}
				onDragEnd={({ active, over }) => {
					setActive(null)
					setIndicator(null)
					stop()
					if (!over) return

					const prevBoard = board

					const activeType = active.data.current?.type
					const overType = over.data.current?.type

					if (activeType === 'card') {
						const fromColId = findColumnIdByCardId(board, active.id)

						const toColId =
							overType === 'card'
								? findColumnIdByCardId(board, over.id)
								: overType === 'column-drop'
									? over.data.current.columnId
									: null

						if (!fromColId || !toColId) return

						const toIndex = overType === 'card' ? board.columns[toColId].cardIds.indexOf(over.id) : board.columns[toColId].cardIds.length

						if (fromColId === toColId) {
							const ids = board.columns[fromColId].cardIds
							const oldIndex = ids.indexOf(active.id)
							const newIndex = toIndex
							if (oldIndex === newIndex) return

							setBoard((prev) => ({
								...prev,
								columns: {
									...prev.columns,
									[fromColId]: {
										...prev.columns[fromColId],
										cardIds: arrayMove(prev.columns[fromColId].cardIds, oldIndex, newIndex),
									},
								},
							}))
						} else {
							setBoard((prev) => {
								const fromIds = [...prev.columns[fromColId].cardIds]
								const toIds = [...prev.columns[toColId].cardIds]

								fromIds.splice(fromIds.indexOf(active.id), 1)
								const insertAt = clamp(toIndex, 0, toIds.length)
								toIds.splice(insertAt, 0, active.id)

								return {
									...prev,
									columns: {
										...prev.columns,
										[fromColId]: { ...prev.columns[fromColId], cardIds: fromIds },
										[toColId]: { ...prev.columns[toColId], cardIds: toIds },
									},
								}
							})
						}

						// Call API here
						const cardData = board.cards[active.id]
						const leadId = cardData?.lead_id || active.id
						changeStatusHandler(leadId, toColId, prevBoard)
					}
				}}
			>
				{isMobile && (
					<MobileColumnNav>
						<MobileNavBtn onClick={() => setCurrentColIdx((i) => i - 1)} disabled={currentColIdx === 0}>
							&#8249;
						</MobileNavBtn>
						<MobileNavBtn onClick={() => setCurrentColIdx((i) => i + 1)} disabled={currentColIdx === columns.length - 1}>
							&#8250;
						</MobileNavBtn>
					</MobileColumnNav>
				)}
				<Board ref={boardRef}>
					<SortableContext items={board.columnOrder} strategy={horizontalListSortingStrategy}>
						<Columns columns={arrangeColumn?.length || columns?.length}>
							{columns.map((col) => {
								const cards = visibleCardsByColumn[col.id] || []
								return (
									<Column
										key={col.id}
										column={col}
										cards={cards}
										registerScrollEl={registerScrollEl}
										indicatorIndex={indicator?.columnId === col.id ? indicator.index : null}
										salesAdmin={salesAdmin}
										update={stableUpdate}
									/>
								)
							})}
						</Columns>
					</SortableContext>
				</Board>

				<DragOverlay>
					{activeCard ? CardTwo({ activeCard }) : activeColumn ? <OverlayColumn>{activeColumn.title}</OverlayColumn> : null}
				</DragOverlay>
			</DndContext>
		</Page>
	)
}
